import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import defaultShield from '@sorare/core/src/assets/club/shield_none.png';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { createVar } from '@sorare/core/src/style/utils';

import { useDefaultStadiumBackground } from '../../../hooks/useDefaultStadiumBackground';
import { LevelPolygon } from '../LevelPolygon';
import { ManagerIdRoot, gradientLevels } from '../ui';
import { getLevelTreshold } from '../utils';
import {
  TinyManagerId_managerProgression,
  TinyManagerId_userSportProfile,
} from './__generated__/index.graphql';

const gradientLevel = createVar();
const background = createVar();

const Root = styled(ManagerIdRoot)`
  --background: var(${background});
  --border: var(${gradientLevel});

  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));

  border-radius: var(--unit);
  background-size: cover;
  background-position: center;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
`;
const Badge = styled.img`
  position: absolute;
  top: calc(40% - var(--double-unit) / 2);
  left: calc(50% - var(--double-unit) / 2);
  height: var(--double-unit);
`;
const Level = styled.div`
  position: absolute;
  bottom: 0;
  text-align: center;

  & > ${LabelS} {
    position: absolute;
    inset: auto 0 0;
    line-height: 15px;
    background: var(${gradientLevel});
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -moz-text-fill-color: transparent;
  }
`;
const StyledLevelPolygon = styled(LevelPolygon)`
  width: 100%;
  height: 15px;
`;

type Props = {
  managerProgression: TinyManagerId_managerProgression | null;
  userSportProfile: TinyManagerId_userSportProfile | null;
};

export const TinyManagerId = ({
  managerProgression,
  userSportProfile,
}: Props) => {
  const defaultStadiumBackground = useDefaultStadiumBackground();

  if (!managerProgression || !userSportProfile) {
    return null;
  }

  const levelTreshold = getLevelTreshold(managerProgression?.levelReached || 0);

  return (
    <Root
      style={{
        [gradientLevel]: gradientLevels[levelTreshold],
        [background]: `url(${
          userSportProfile.background?.pictureUrl || defaultStadiumBackground
        })`,
      }}
    >
      <Badge
        src={userSportProfile.clubBadge?.pictureUrl || defaultShield}
        alt={userSportProfile.clubBadge?.name || ''}
      />
      <Level>
        <LabelS as="p" bold>
          {managerProgression.levelReached}
        </LabelS>
        <StyledLevelPolygon level={managerProgression.levelReached} />
      </Level>
    </Root>
  );
};

TinyManagerId.fragments = {
  managerProgression: gql`
    fragment TinyManagerId_managerProgression on ManagerProgression {
      id
      levelReached
    }
  ` as TypedDocumentNode<TinyManagerId_managerProgression>,
  userSportProfile: gql`
    fragment TinyManagerId_userSportProfile on UserSportProfileInterface {
      id
      clubName
      clubBadge {
        id
        pictureUrl
        name
      }
      background {
        id
        pictureUrl
        name
      }
    }
  ` as TypedDocumentNode<TinyManagerId_userSportProfile>,
};
